<template>
  <v-dialog v-model="internalValue" max-width="560" @keydown.esc="close">
    <v-card>
      <v-card-title class="text-h6">
        {{ dialogTitle }}
      </v-card-title>

      <v-card-text>
        <div v-if="isCopyAction && isLoadingPlans" class="mb-3">
          <v-progress-circular
            indeterminate
            :size="20"
            color="primary"
            class="mr-2"
          ></v-progress-circular>
          Loading plans...
        </div>

        <v-select
          v-if="isCopyAction && !isLoadingPlans"
          v-model="selectedPlan"
          :items="schemeList"
          item-value="Id"
          item-text="ShortName"
          label="Select plan"
          dense
          outlined
          class="mb-3"
        ></v-select>

        <div v-if="isCopyAction">
          Copy currently selected calculations.
        </div>

        <div v-else-if="isExportAction">
          Export selected calculations.
        </div>

        <div v-else-if="isImportAction" class="pt-2">
          <v-file-input
            v-model="uploadFile"
            :disabled="loading"
            label="File name"
            accept=".xml,text/xml"
            dense
          ></v-file-input>
        </div>

        <v-alert v-if="errorMessage" type="error" dense outlined class="mt-3 mb-0">
          {{ errorMessage }}
        </v-alert>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" outlined :disabled="loading" @click="close">Cancel</v-btn>
        <v-btn color="primary" :loading="loading" @click="onConfirm">Confirm</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'CalculationActionsPopup',

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    buttonType: {
      type: String,
      default: null,
    },
    calcLevel: {
      type: Number,
      default: null,
    },
    calcType: {
      type: String,
      default: null,
    },
    currentPlanId: {
      type: Number,
      default: null,
    },
    selectedItems: {
      type: Array,
      default: () => [],
    },
    calcGroup: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      loading: false,
      isLoadingPlans: false,
      uploadFile: null,
      schemeList: [],
      selectedPlan: null,
      errorMessage: '',
    };
  },

  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },

    dialogTitle() {
      if (this.isCopyAction) return 'Copy Calculation';
      if (this.isExportAction) return 'Export Calculations';
      if (this.isImportAction) return 'Import Calculations';
      return 'Calculation Action';
    },

    normalizedActionType() {
      return (this.buttonType || '').toString().trim().toLowerCase();
    },

    isCopyAction() {
      return this.normalizedActionType === 'copy';
    },

    isExportAction() {
      return this.normalizedActionType === 'export';
    },

    isImportAction() {
      return this.normalizedActionType === 'import';
    },
  },

  watch: {
    value(val) {
      if (val) {
        this.errorMessage = '';
        this.selectedPlan =
          this.calcLevel === 0 ? 0 : this.calcLevel != null ? this.calcLevel : this.currentPlanId;

        if (this.isCopyAction) {
          this.getSchemeList();
        } else {
          this.isLoadingPlans = false;
          this.schemeList = [];
        }
      } else {
        this.uploadFile = null;
      }
    },
  },

  methods: {
    close() {
      this.internalValue = false;
      this.errorMessage = '';
      this.loading = false;
      this.uploadFile = null;
    },

    async onConfirm() {
      this.errorMessage = '';

      if (this.selectedPlan == null) {
        this.errorMessage = 'Please select a plan';
        return;
      }

      if (this.isCopyAction) {
        await this.copyCalculation();
        return;
      }

      if (this.isExportAction) {
        await this.exportCalculations();
        return;
      }

      if (this.isImportAction) {
        await this.importCalculations();
      }
    },

    async copyCalculation() {
      const list = this.selectedItems.map((item) => item.Id);

      if (!list.length) {
        this.errorMessage = 'Please select at least one item to copy';
        return;
      }

      this.loading = true;

      try {
        const data = {
          PlanId: this.selectedPlan,
          GroupId: this.calcGroup ?? 0,
          CalcType: this.calcType,
          Calculation: list,
        };

        await this.$api.post('/Calculation/CopyCalculations', data);

        this.$store.commit('setNotificationMessage', {
          type: 'success',
          text: 'Copy completed',
        });

        this.$emit('completed', { type: 'copy' });
        this.close();
      } catch (err) {
        this.errorMessage = err && err.message ? err.message : 'Copy failed';
        console.log(err);
      } finally {
        this.loading = false;
      }
    },

    async exportCalculations() {
      const list = [];

      if (this.selectedItems.length > 0) {
        this.selectedItems.forEach((item) => {
          list.push(item.Id);
        });
      }

      if (!list.length) {
        this.errorMessage = 'Please select at least one item to export';
        return;
      }

      this.loading = true;

      try {
        const data = {
          PlanId: this.calcLevel === 0 ? 0 : this.selectedPlan,
          GroupId: this.calcGroup ?? 0,
          CalcType: this.calcType,
          Calculation: list,
        };

        const response = await this.$api({
          method: 'POST',
          url: '/Calculation/ExportCalculations',
          data: data,
          responseType: 'blob',
        });

        if (!response.data) {
          const msg = 'Unable to get download path';
          this.$store.commit('setNotificationMessage', { type: 'error', text: msg });
          throw new Error(msg);
        }

        const url = window.URL.createObjectURL(
          new Blob([response.data], {
            type: 'text/xml',
          })
        );

        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'calculations.xml');
        document.body.appendChild(link);
        link.click();

        this.$store.commit('setNotificationMessage', {
          type: 'success',
          text: 'Export completed',
        });

        this.$emit('completed', { type: 'export' });
        this.close();
      } catch (err) {
        this.errorMessage = err && err.message ? err.message : 'Export failed';
        console.log(err);
      } finally {
        this.loading = false;
      }
    },

    async importCalculations() {
      if (!this.uploadFile) {
        this.errorMessage = 'Please select a file to import';
        return;
      }

      this.loading = true;

      try {
        const data = new FormData();
        data.append('planId', this.calcLevel === 0 ? 0 : this.selectedPlan);
        data.append('File', this.uploadFile);

        await this.$api({
          url: '/calculation/ImportCalculations',
          method: 'POST',
          data: data,
        });

        this.$store.commit('setNotificationMessage', {
          type: 'success',
          text: 'Import completed',
        });

        this.$emit('completed', { type: 'import' });
        this.close();
      } catch (err) {
        this.errorMessage = err && err.message ? err.message : 'Import failed';
        console.log(err);
      } finally {
        this.loading = false;
      }
    },

    async getSchemeList() {
      const systemPlan = { Id: 0, ShortName: 'System' };

      try {
        this.isLoadingPlans = true;
        this.schemeList = [systemPlan];
        const response = await this.$api.get('/plans/getplanlist');

        if (!response.data || !response.data.length) {
          this.errorMessage = 'Unable to load plans';
          return;
        }

        this.schemeList = [
          systemPlan,
          ...response.data.filter((plan) => Number(plan.Id) !== systemPlan.Id),
        ];

        const hasSelectedPlan = this.schemeList.some((plan) => plan.Id === this.selectedPlan);

        if (!hasSelectedPlan) {
          if (this.currentPlanId && this.schemeList.some((plan) => plan.Id === this.currentPlanId)) {
            this.selectedPlan = this.currentPlanId;
          } else {
            this.selectedPlan = this.schemeList[0].Id;
          }
        } else if (!this.selectedPlan) {
          this.selectedPlan = this.schemeList[0].Id;
        }
      } catch (err) {
        this.errorMessage = err && err.message ? err.message : 'Unable to load plans';
        console.log(err);
      } finally {
        this.isLoadingPlans = false;
      }
    },
  },
};
</script>
